import { useEffect, useMemo, useRef, useState } from 'react';
import Notice from '../../components/Notice.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import StatusWord, { Dot } from '../../components/StatusWord.jsx';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import ConsoleTabs, { panelProps } from '../../components/ConsoleTabs.jsx';
import ExternalLink from '../../components/ExternalLink.jsx';
import { daysLeftWords, dateOnlyWords, lastSignInWords, personName, plural, savedLine, shortDate } from '../../lib/access-review-format.js';
import { DECISION_WORDS, GLOBAL_ADMINISTRATOR, TEXT_MAX, TEXT_MIN, quickReasonLabel, validateDecision } from '../../../../shared/access-reviews.mjs';
import { groupByPerson, groupByRole, isGroupable, isWorkspaceView, keepAllEligible } from './workspace-groups.js';
import PeoplePicker, { pickedId, pillError } from './PeoplePicker.jsx';

const DECISION_ORDER = Object.freeze(['keep', 'remove', 'not-sure']);
const codesOf = (item) => (Array.isArray(item?.signals) ? item.signals.map((s) => (typeof s === 'string' ? s : s?.code)).filter(Boolean) : []);
const justId = (ref) => `review-why-${ref}`;
const KIND_WORDS = Object.freeze({ active: 'Active', eligible: 'Eligible' });
export const GROUPING_KEY = 'onguard.accessReviews.grouping';
// A signed-in non-admin cannot receive the one-time code that a Keep on these roles needs (spec 2026-10-29 section 2
// item 7): the API refuses it (email-link-required), so the page offers Remove and Not sure and points to the email.
export const KEEP_NEEDS_LINK = 'To keep this role, use the button in your review email: a Keep on Global Administrator or Privileged Role Administrator needs a one-time code sent to your email address.';
const SKIPPED_TAIL = 'Global Administrator, Privileged Role Administrator, flagged and your own assignments need one decision each.';
const BY_VERB = Object.freeze({ keep: 'Kept', remove: 'Removal chosen', 'not-sure': 'Passed on' });
const CHIP = 'inline-flex min-h-[36px] items-center rounded-sm border px-3.5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-ipc-light';
const CHIP_ON = 'border-ipc-primary bg-ipc-primary text-white';
const CHIP_OFF = 'border-ipc-field bg-white text-ipc-ink hover:bg-ipc-surface';

/** The remembered tab: 'role' or 'person' (the default). The browser may refuse storage, so this never throws. */
function readGrouping() {
  try { return localStorage.getItem(GROUPING_KEY) === 'role' ? 'role' : 'person'; } catch { return 'person'; }
}
function writeGrouping(value) {
  try { localStorage.setItem(GROUPING_KEY, value); } catch { /* the choice is only a convenience */ }
}

/** One decision checked in the browser with the shared rule, plus the item's own `needsText` (the API checks again). */
export function checkDecision(item, draft, { selfReview = false } = {}) {
  // An item already handed on (canAsk false) cannot be handed on again: its Not sure goes to the administrators. The
  // colleague is a chip from the people picker; only its directory id is sent (spec 2026-10-29 section 2 item 6).
  const asking = item.canAsk !== false && draft.decision === 'not-sure' && draft.noOne !== true && Array.isArray(draft.askPeople) && draft.askPeople.length > 0;
  if (asking && pillError(draft.askPeople)) return { ok: false, error: `${pillError(draft.askPeople)} Remove them and choose someone else, or choose I don't know who.` };
  const ask = asking && pickedId(draft.askPeople) ? { askToId: pickedId(draft.askPeople) } : {};
  const v = validateDecision({ decision: draft.decision, reasonId: draft.reasonId || null, text: draft.text, ...ask }, { roleTemplateId: item.roleTemplateId, signals: codesOf(item) }, { selfReview });
  if (!v.ok) return v;
  if (item.needsText && draft.decision !== 'not-sure' && Array.from((draft.text ?? '').trim()).length < TEXT_MIN) {
    return { ok: false, error: `This assignment is flagged: write why in at least ${TEXT_MIN} characters.` };
  }
  return v;
}

/** The words of an HTTP refusal of a decision, a code or a delegation. */
function failureWords(r, fallback) {
  if (r?.status === 429) return 'Too many attempts. Try again in a minute.';
  return r?.body?.message ?? fallback;
}

/**
 * The reviewer's list (spec 13.2), shared by the email link (ReviewPage) and the signed-in view (MyReviewsPage). A page
 * load never records anything: the decision from the link is only preselected, and the reviewer presses Save.
 *
 * `adapter` sends the calls: decide({ decisions, bulk?, code? }), delegate({ itemRefs, toId, reason }), searchPeople({ q,
 * item }) (the people picker), and for the email link requestCode(). Each resolves with { status, body }. `onGone(body)` hands a 410 back to the page.
 */
/** Undecided and assigned to another reviewer (admin payload: `assignedToMe` on every item). */
const waitingOnOthers = (i) => !i.decision && i.assignedToMe === false;

export default function ReviewWorkspace({ view, adapter, preselect = null, onGone, embedded = false }) {
  const [items, setItems] = useState(() => view.items ?? []);
  const [drafts, setDrafts] = useState(() => {
    const out = {};
    const target = preselect?.itemRef ? (view.items ?? []).find((i) => i.itemRef === preselect.itemRef) : null;
    if (target && preselect.decision && !target.decision) out[target.itemRef] = { decision: preselect.decision, reasonId: '', text: '' };
    return out;
  });
  const [selected, setSelected] = useState(() => new Set());
  const [bulk, setBulk] = useState(null);
  const [code, setCode] = useState({ sent: false, value: '', verified: view.codeVerified === true, itemRef: null, message: null });
  const [delegating, setDelegating] = useState(null);
  const [status, setStatus] = useState(null);
  const [selectNote, setSelectNote] = useState(null);
  const [delegateFailures, setDelegateFailures] = useState([]);
  const [grouping, setGroupingState] = useState(readGrouping);
  const [undecidedOnly, setUndecidedOnly] = useState(() => isWorkspaceView({ state: 'ready', ...view }));
  const [query, setQuery] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  // Admin: only the undecided items assigned to another reviewer ("Review the N", decided on their behalf).
  const [othersOnly, setOthersOnly] = useState(false);
  const codeRef = useRef(null);
  const bulkRef = useRef(null);
  const emailLink = view.method === 'email-link';
  const kindShown = view.kindShown === true;
  // admin: the server-proven admin payload (every item, with its reviewer). grouped: any payload that names the holder.
  const admin = isWorkspaceView({ state: 'ready', ...view });
  const grouped = isGroupable(view.items);
  // `ownAccess` comes from the server, which resolves the holder and the viewer's addresses the way a decision is checked.
  const isOwn = (i) => i.ownAccess === true;
  const bulkOk = (i) => keepAllEligible([i]).eligible.length === 1;
  const setGrouping = (value) => { setGroupingState(value); writeGrouping(value); };

  // The row the email link points at: scroll to it and focus its justification. Nothing is sent.
  useEffect(() => {
    if (!preselect?.itemRef) return;
    const el = document.getElementById(justId(preselect.itemRef));
    if (!el) return;
    el.scrollIntoView?.({ block: 'center' });
    el.focus?.();
    // Once, on the first render of the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saved = items.filter((i) => i.decision).length;
  const total = items.length;
  const q = query.trim().toLowerCase();
  const matches = (i) => !q || (grouping === 'person' ? `${i.holder?.displayName ?? ''} ${i.holder?.upn ?? ''}` : `${i.role ?? ''}`).toLowerCase().includes(q);
  const shown = useMemo(
    () => (grouped ? items.filter((i) => (!undecidedOnly || !i.decision) && (!mineOnly || i.assignedToMe) && (!othersOnly || waitingOnOthers(i)) && matches(i)) : items),
    [grouped, items, undecidedOnly, mineOnly, othersOnly, query, grouping],
  );
  const undecided = shown.filter((i) => !i.decision);
  const othersWaiting = admin && grouped ? items.filter(waitingOnOthers).length : 0;
  const overdue = view.campaign?.state === 'overdue' || (Number.isInteger(view.campaign?.daysLeft) && view.campaign.daysLeft < 0);
  const reviewOthers = () => {
    setOthersOnly(true);
    setMineOnly(false);
    document.querySelector('[data-testid="review-items"]')?.scrollIntoView?.({ block: 'start' });
  };
  const eligible = undecided.filter(bulkOk);
  const ineligibleShown = undecided.length > eligible.length;
  const due = shortDate(view.campaign?.dueAt);
  const requester = view.campaign?.requestedBy;
  const people = useMemo(() => (grouped && grouping === 'person' ? groupByPerson(shown) : []), [grouped, grouping, shown]);
  const roles = useMemo(() => (grouped && grouping === 'role' ? groupByRole(shown) : []), [grouped, grouping, shown]);
  const tabCounts = useMemo(() => (grouped ? { person: groupByPerson(shown).length, role: groupByRole(shown).length } : null), [grouped, shown]);

  const setDraft = (ref, patch) => setDrafts((d) => ({ ...d, [ref]: { decision: null, reasonId: '', text: '', ...(d[ref] ?? {}), ...patch } }));
  const recorded = (decisions, answerItems) => {
    const versions = new Map((answerItems ?? []).map((x) => [x.itemRef, x.version]));
    const now = new Date().toISOString();
    setItems((list) => list.map((it) => {
      const d = decisions.find((x) => x.itemRef === it.itemRef);
      if (!d) return it;
      const mine = admin ? { decidedBy: view.reviewer?.displayName ?? null, onBehalfOf: it.assignedToMe ? null : (it.reviewer?.displayName ?? null), selfReview: it.ownAccess ?? false } : {};
      return {
        ...it,
        ...mine,
        version: versions.get(it.itemRef) ?? it.version,
        decision: { decision: d.decision, reasonId: d.reasonId ?? null, reasonLabel: quickReasonLabel(d.decision, d.reasonId) ?? null, text: d.text || null, decidedAt: now, method: view.method, changes: (it.decision?.changes ?? 0) + (it.decision ? 1 : 0) },
      };
    }));
    setDrafts((dr) => { const n = { ...dr }; for (const d of decisions) delete n[d.itemRef]; return n; });
    setSelected((s) => { const n = new Set(s); for (const d of decisions) n.delete(d.itemRef); return n; });
  };

  const send = async (decisions, { bulk: isBulk = false, withCode = null } = {}) => {
    const r = await adapter.decide({ decisions, ...(isBulk ? { bulk: true } : {}), ...(withCode ? { code: withCode } : {}) });
    if (r.status === 200 && r.body?.state === 'recorded') {
      recorded(decisions, r.body.items);
      if (withCode) setCode((c) => ({ ...c, verified: true, sent: false, value: '', itemRef: null, message: null }));
      return { ok: true };
    }
    if (r.status === 410) { onGone?.(r.body); return { ok: false, error: null }; }
    if (r.status === 409) return { ok: false, stale: true, error: r.body?.message ?? 'This assignment changed since you opened the page. Reload to see the latest.' };
    if (r.status === 403 && r.body?.state === 'code-required') return { ok: false, needCode: true, error: r.body?.message ?? 'This role needs the one-time code from your email.' };
    return { ok: false, error: failureWords(r, 'Your decision was not saved. Try again.') };
  };

  const requestCode = async (itemRef) => {
    const r = await adapter.requestCode();
    if (r.status === 200 && r.body?.state === 'sent') {
      setCode((c) => ({ ...c, sent: true, value: '', itemRef, message: null }));
      setTimeout(() => codeRef.current?.focus?.(), 0);
      return true;
    }
    if (r.status === 410) { onGone?.(r.body); return false; }
    setDraft(itemRef, { error: failureWords(r, 'The code could not be sent. Try again in a moment.') });
    return false;
  };

  const saveRow = async (item) => {
    const draft = drafts[item.itemRef] ?? {};
    const v = checkDecision(item, draft, { selfReview: isOwn(item) });
    if (!v.ok) { setDraft(item.itemRef, { error: v.error }); return; }
    const stepUp = emailLink && item.stepUp && draft.decision === 'keep' && !code.verified;
    let withCode = null;
    if (stepUp) {
      if (!(code.sent && code.itemRef === item.itemRef)) {
        setDraft(item.itemRef, { error: null, busy: true });
        await requestCode(item.itemRef);
        setDraft(item.itemRef, { busy: false });
        return;
      }
      if (!/^\d{6}$/.test(code.value.trim())) { setDraft(item.itemRef, { error: 'Type the 6-digit code from the email.' }); return; }
      withCode = code.value.trim();
    }
    setDraft(item.itemRef, { error: null, busy: true });
    try {
      const out = await send([{ itemRef: item.itemRef, version: item.version, decision: v.value.decision, ...(v.value.reasonId ? { reasonId: v.value.reasonId } : {}), ...(v.value.text ? { text: v.value.text } : {}), ...(v.value.askToId ? { askToId: v.value.askToId } : {}) }], { withCode });
      if (out.ok) { if (grouped) setStatus(`Saved ${item.holder?.displayName ?? 'the assignment'}, ${item.role}.`); return; }
      if (out.needCode && emailLink) { setDraft(item.itemRef, { busy: false, error: out.error }); if (!code.sent) await requestCode(item.itemRef); return; }
      setDraft(item.itemRef, { busy: false, error: out.error, stale: out.stale === true });
    } catch {
      setDraft(item.itemRef, { busy: false, error: 'The onGuard portal could not be reached. Your choice is still on the page: press Save again.' });
    }
  };

  // The selection the action bar works on: undecided rows only. Keep and Remove take the rows a bulk decision may cover
  // (the checkbox is off for the others); Delegate takes the rows that can still be handed on.
  const selectedOpen = items.filter((i) => selected.has(i.itemRef) && !i.decision);
  const bulkSelected = selectedOpen.filter(bulkOk);
  const saveBulk = async () => {
    const chosen = bulkSelected;
    if (!bulk?.decision || chosen.length === 0) return;
    const decisions = [];
    for (const it of chosen) {
      const v = checkDecision(it, bulk, { selfReview: isOwn(it) });
      if (!v.ok) { setBulk((b) => ({ ...b, error: v.error })); return; }
      decisions.push({ itemRef: it.itemRef, version: it.version, decision: v.value.decision, ...(v.value.reasonId ? { reasonId: v.value.reasonId } : {}), ...(v.value.text ? { text: v.value.text } : {}) });
    }
    setBulk((b) => ({ ...b, busy: true, error: null }));
    try {
      const out = await send(decisions, { bulk: true });
      if (out.ok) { setBulk(null); setStatus(`Saved ${plural(decisions.length, 'decision')}.`); return; }
      setBulk((b) => ({ ...b, busy: false, error: out.error }));
    } catch {
      setBulk((b) => ({ ...b, busy: false, error: 'The onGuard portal could not be reached. Try again.' }));
    }
  };

  const selectAll = () => {
    const skipped = undecided.length - eligible.length;
    setSelected(new Set(eligible.map((i) => i.itemRef)));
    setSelectNote(`Selected ${plural(eligible.length, 'assignment')}${skipped > 0 ? `; skipped ${skipped} that ${skipped === 1 ? 'needs' : 'need'} one decision each` : ''}.`);
  };
  const keepAll = (groupKey, groupItems) => {
    const { eligible: ok } = keepAllEligible(groupItems);
    if (ok.length === 0) return;
    setSelected(new Set(ok.map((i) => i.itemRef)));
    setSelectNote(null);
    setBulk({ decision: 'keep', reasonId: '', text: '', groupKey });
  };
  const openBulk = (decision) => {
    setBulk({ decision, reasonId: '', text: '' });
    setTimeout(() => bulkRef.current?.focus?.(), 0);
  };
  const clearSelection = () => { setSelected(new Set()); setBulk(null); setSelectNote(null); setDelegateFailures([]); };
  const toggle = (ref) => setSelected((s) => { const n = new Set(s); if (n.has(ref)) n.delete(ref); else n.add(ref); return n; });

  // Delegate needs the people picker: the colleague is a directory id, never typed text.
  const handsOver = Boolean(adapter.delegate && adapter.searchPeople);
  // One hop: the API says per item whether it can still be handed on (canDelegate false once handed on, admins included).
  const canHand = (i) => !i.decision && (i.canDelegate === true || (admin && i.canDelegate !== false));
  const delegable = useMemo(() => items.filter(canHand), [items, admin]); // eslint-disable-line react-hooks/exhaustive-deps
  const delegableSelected = selectedOpen.filter(canHand);
  const describe = (ref) => { const i = items.find((x) => x.itemRef === ref); return i ? `${personName(i.holder, 'Unknown principal')}, ${i.role}` : 'One assignment'; };
  const onDelegated = (itemRefs, to, failures = [], emailRetry = false) => {
    const name = personName(to, 'your colleague');
    // The items moved even when the email could not go out now: the API queued it, and the reviewer is told.
    const mail = emailRetry ? `The email to ${name} could not be sent just now; it will be retried.` : 'They get one email.';
    setDelegateFailures(failures.map((f) => ({ ...f, label: describe(f.itemRef) })));
    if (admin) {
      // One hop: a moved item cannot be handed on again, so a later Delegate selected never re-sends it.
      setItems((list) => list.map((i) => (itemRefs.includes(i.itemRef) ? { ...i, reviewer: { id: null, displayName: name }, assignedToMe: false, canDelegate: false } : i)));
      setStatus(`Handed ${plural(itemRefs.length, 'assignment')} to ${name}. ${mail}`);
    } else {
      setItems((list) => list.filter((i) => !itemRefs.includes(i.itemRef)));
      setStatus(`Handed ${plural(itemRefs.length, 'assignment')} to ${name}. ${emailRetry ? mail : 'They get one email;'} ${emailRetry ? 'Nothing else is needed from you for' : 'nothing else is needed from you for'} ${itemRefs.length === 1 ? 'it' : 'them'}.`);
    }
    setSelected((s) => { const n = new Set(s); for (const r of itemRefs) n.delete(r); return n; });
    setDelegating(null);
  };

  const Heading = embedded ? 'h2' : 'h1';
  const rowProps = (item) => ({
    item, draft: drafts[item.itemRef], kindShown, view, admin, own: isOwn(item), actorName: view.reviewer?.displayName ?? null,
    checked: selected.has(item.itemRef), onToggle: () => toggle(item.itemRef),
    onDraft: (patch) => setDraft(item.itemRef, patch), onSave: () => saveRow(item),
    onChange: () => setDraft(item.itemRef, { decision: item.decision.decision, reasonId: item.decision.reasonId ?? '', text: item.decision.text ?? '', editing: true }),
    onDelegate: handsOver && canHand(item) ? () => setDelegating([item.itemRef]) : null,
    code: emailLink && item.stepUp && drafts[item.itemRef]?.decision === 'keep' && code.sent && code.itemRef === item.itemRef && !code.verified ? code : null,
    codeRef, onCode: (value) => setCode((c) => ({ ...c, value })), onResend: () => requestCode(item.itemRef),
    requester, tenantName: view.tenantName,
    search: adapter.searchPeople ? (q) => adapter.searchPeople({ q, item: item.itemRef }) : null,
  });
  const bulkPanel = bulk ? (
    <div ref={bulkRef} tabIndex={-1} className="focus:outline-none">
    <SectionCard ariaLabel="Decision for the selected assignments" bodyClassName="space-y-3" testId="review-bulk-panel">
      <p className="text-sm font-semibold text-ipc-ink">{DECISION_WORDS[bulk.decision]} {plural(bulkSelected.length, 'selected assignment')}: one reason for all.</p>
      <Justification
        id="review-why-bulk" decision={bulk.decision} draft={bulk} reasons={view.quickReasons?.[bulk.decision] ?? []} required={false}
        onChange={(patch) => setBulk((b) => ({ ...b, ...patch }))}
      />
      {bulk.error ? <p role="alert" className="text-sm"><StatusWord tone="bad" strong={false}>{bulk.error}</StatusWord></p> : null}
      <div className="flex flex-wrap gap-2.5">
        <button type="button" className="btn-primary min-h-[40px]" disabled={bulk.busy} onClick={saveBulk} data-testid="review-bulk-save">{bulk.busy ? 'Saving' : `Save for ${bulkSelected.length}`}</button>
        <button type="button" className="btn-secondary min-h-[40px]" onClick={() => setBulk(null)}>Cancel</button>
      </div>
    </SectionCard>
    </div>
  ) : null;
  // A Keep all panel opens inside its own group; after a tab change (or from the action bar) it sits in the action bar.
  const panelInGroup = (key) => (bulk?.groupKey === key ? bulkPanel : null);
  const panelInBar = bulk && !(bulk.groupKey && bulk.groupKey.startsWith(`${grouping}:`)) ? bulkPanel : null;

  const groupHeader = (key, title, sub, groupItems, help, facts = null) => {
    const { eligible: ok, skipped } = keepAllEligible(groupItems);
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="min-w-0">
            <h3 className="break-words text-base font-semibold text-ipc-ink">{title}</h3>
            {sub ? <p className="break-all text-[12.5px] text-ipc-muted">{sub}</p> : null}
          </div>
          {ok.length > 0 ? (
            <button type="button" className="tap ml-auto text-sm font-semibold text-ipc-light hover:underline" onClick={() => keepAll(key, groupItems)}>Keep all</button>
          ) : null}
        </div>
        {facts}
        {skipped > 0 ? <p className="text-[13px] text-ipc-muted" data-testid="review-keep-all-skipped">Skipped {skipped}: {SKIPPED_TAIL}</p> : null}
        {help}
        {panelInGroup(key)}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0">
          <Heading className="break-words">{admin ? `Review ${plural(view.progress?.total ?? total, 'privileged role assignment')}` : `Your review: ${plural(view.progress?.total ?? total, 'privileged role assignment')}`}</Heading>
          <p className="mt-1.5 text-[15px] text-ipc-muted" data-testid="review-subline">
            For {view.tenantName}, due {due}{Number.isInteger(view.campaign?.daysLeft) ? ` (${daysLeftWords(view.campaign.daysLeft)})` : ''}.
            {requester ? ` Requested by ${personName(requester)}${requester.address ? ` (${requester.address})` : ''}.` : ''}
            {admin ? ' You can decide any row as an admin; each row shows who it is assigned to.' : ' Decide each one; you can change a decision until the review closes.'}
          </p>
        </div>
        {handsOver && !admin && delegable.length > 0 ? (
          <button type="button" className="btn-secondary min-h-[40px] sm:ml-auto" onClick={() => setDelegating(delegable.map((i) => i.itemRef))} data-testid="review-delegate-all">Delegate</button>
        ) : null}
      </div>

      <p className="text-sm font-semibold text-ipc-ink" role="status" data-testid="review-progress">{saved} of {total} saved</p>
      {othersWaiting > 0 ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ipc-ink" data-testid="review-waiting-others" data-tone={overdue ? 'warn' : 'info'}>
          <span className="flex items-center gap-2">
            <Dot tone={overdue ? 'warn' : 'info'} />
            <span className={overdue ? 'font-semibold' : undefined}>{plural(othersWaiting, 'assignment')} {othersWaiting === 1 ? 'is' : 'are'} waiting on other reviewers{overdue ? ', past the due date' : ''}. You can decide them for the assigned reviewer.</span>
          </span>
          <button type="button" className={`${overdue ? 'btn-primary' : 'btn-secondary'} min-h-[40px]`} onClick={reviewOthers}>Review the {othersWaiting}</button>
        </div>
      ) : null}
      {(view.redirectedOwn ?? []).map((r) => (
        <p key={r.role} className="flex items-center gap-2 text-[13.5px] text-ipc-muted" data-testid="review-own">
          <Dot tone="info" />Your own assignment ({r.role}) went to another reviewer: nobody reviews their own access.
        </p>
      ))}
      {status ? <p className="text-sm text-ipc-ink" role="status" data-testid="review-status">{status}</p> : null}
      {admin && total > 0 && saved === total ? (
        <Notice tone="good" title={`Nothing left to decide. You can change a decision until ${due}.`} testId="review-done" />
      ) : null}
      {!admin && total > 0 && saved === total ? (
        <Notice tone="good" title={`All ${total} saved.`} testId="review-done">
          You can change a decision until {due}. Nothing else is needed from you.
        </Notice>
      ) : null}

      {grouped ? (
        <div className="space-y-3">
          <ConsoleTabs
            label="Group the assignments" idPrefix="review-grouping" value={grouping} onChange={setGrouping}
            items={[{ id: 'person', label: `By person (${tabCounts.person})` }, { id: 'role', label: `By role (${tabCounts.role})` }]}
          />
          <div role="group" aria-label="Filter the assignments" className="flex flex-wrap gap-2">
            <button type="button" aria-pressed={undecidedOnly} className={`${CHIP} ${undecidedOnly ? CHIP_ON : CHIP_OFF}`} onClick={() => setUndecidedOnly((v) => !v)}>Undecided only</button>
            {admin ? <button type="button" aria-pressed={mineOnly} className={`${CHIP} ${mineOnly ? CHIP_ON : CHIP_OFF}`} onClick={() => { setMineOnly((v) => !v); setOthersOnly(false); }}>Assigned to me</button> : null}
            {admin ? <button type="button" aria-pressed={othersOnly} className={`${CHIP} ${othersOnly ? CHIP_ON : CHIP_OFF}`} onClick={() => { setOthersOnly((v) => !v); setMineOnly(false); }}>Waiting on others</button> : null}
          </div>
          <label className="block max-w-sm">
            <span className="label">{grouping === 'person' ? 'Search people' : 'Search roles'}</span>
            <input type="search" className="input" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="review-search" />
          </label>
        </div>
      ) : null}

      {undecided.length > 0 ? (
        <div className="space-y-1.5" data-testid="review-bulk">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            {selected.size === 0 && eligible.length > 0 ? (
              <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={selectAll} data-testid="review-select-all">Select all</button>
            ) : null}
          </div>
          {selectNote ? <p className="text-[13px] text-ipc-muted" data-testid="review-select-note">{selectNote}</p> : null}
          {ineligibleShown && !admin ? <p className="text-[13px] text-ipc-muted" data-testid="review-bulk-excluded">{view.bulkExcludedLine}</p> : null}
        </div>
      ) : null}

      {grouped ? (
        <div {...panelProps('review-grouping', grouping)} className="space-y-5" data-testid="review-items">
          {grouping === 'person'
            ? people.map((g) => {
              const key = `person:${g.holderId}`;
              return (
                <section key={key} aria-label={personName(g.holder, 'Unknown principal')} className="space-y-3">
                  {groupHeader(key, personName(g.holder, 'Unknown principal'), g.holder?.upn, g.items, null, <HolderFacts holderId={g.holderId} items={items} first={g.items[0]} />)}
                  <ul className="space-y-3">{g.items.map((item) => <ReviewRow key={item.itemRef} {...rowProps(item)} showRoleHelp />)}</ul>
                </section>
              );
            })
            : roles.map((g) => {
              const key = `role:${g.roleTemplateId}`;
              return (
                <section key={key} aria-label={g.role} className="space-y-3">
                  {groupHeader(key, g.role, `${plural(g.items.length, 'assignment')}`, g.items, <><RoleHelp help={g.roleHelp} />{g.roleTemplateId === GLOBAL_ADMINISTRATOR ? <p className="text-[13px] text-ipc-muted" data-testid="review-ga-guidance">Microsoft guidance: 2 to 4</p> : null}</>)}
                  <ul className="space-y-3">{g.items.map((item) => <ReviewRow key={item.itemRef} {...rowProps(item)} showRoleHelp={false} />)}</ul>
                </section>
              );
            })}
          {shown.length === 0 && total > saved ? <p className="text-sm text-ipc-muted" data-testid="review-empty-filter">No assignment matches these filters.</p> : null}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="review-items">
          {items.map((item) => <ReviewRow key={item.itemRef} {...rowProps(item)} showRoleHelp />)}
        </ul>
      )}
      <p className="text-[13px] text-ipc-muted">
        onGuard never changes access. Your decisions, with your name and the time, go into {view.tenantName}'s review evidence.
        {handsOver && !admin && delegable.length > 0 ? ' Not the right person? Use Delegate and name a colleague.' : ''}
      </p>

      {delegateFailures.length > 0 ? (
        <div role="alert" className="text-sm" data-testid="review-delegate-failures">
          <p><StatusWord tone="bad" strong={false}>Not handed over ({delegateFailures.length}); {admin ? 'they stay with their current reviewer and stay selected' : 'they are still yours and still selected'}:</StatusWord></p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ipc-ink">
            {delegateFailures.map((f) => <li key={f.itemRef}>{f.label}: {f.message}</li>)}
          </ul>
        </div>
      ) : null}

      {selectedOpen.length > 0 ? (
        // The action bar stays at the bottom of the window while the reviewer scrolls the list.
        <div
          role="region" aria-label="Selected assignments" data-testid="review-action-bar"
          className="sticky bottom-0 z-20 max-h-[80vh] space-y-3 overflow-y-auto border-t border-ipc-border bg-white py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
        >
          {panelInBar}
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-sm font-semibold text-ipc-ink" data-testid="review-selected">{selectedOpen.length} selected</span>
            {bulkSelected.length > 0 ? (
              <>
                <button type="button" className="btn-primary min-h-[40px]" onClick={() => openBulk('keep')}>Keep selected ({bulkSelected.length})</button>
                <button type="button" className="btn-secondary min-h-[40px]" onClick={() => openBulk('remove')}>Remove selected ({bulkSelected.length})</button>
              </>
            ) : null}
            {handsOver && delegableSelected.length > 0 ? (
              <button type="button" className="btn-secondary min-h-[40px]" onClick={() => setDelegating(delegableSelected.map((i) => i.itemRef))}>Delegate selected ({delegableSelected.length})</button>
            ) : null}
            <button type="button" className="tap text-sm font-semibold text-ipc-light hover:underline" onClick={clearSelection}>Clear selection</button>
          </div>
        </div>
      ) : null}

      {delegating ? (
        <DelegateDialog itemRefs={delegating} adapter={adapter} describe={describe} onClose={() => setDelegating(null)} onDone={onDelegated} onGone={onGone} />
      ) : null}
    </>
  );
}

/** What the payload knows about one holder: the last sign-in and how many privileged roles they hold in this review. */
function HolderFacts({ holderId, items, first }) {
  const count = items.filter((i) => (i.holderId ?? null) === holderId).length;
  return (
    <p className="text-[13px] text-ipc-muted" data-testid="review-holder-facts">
      {plural(count, 'privileged role')}. Last sign-in: {lastSignInWords(first?.lastSignInAt)}.
    </p>
  );
}

/** "What this role can do": plain words for what the role does and what is at risk, with the Microsoft Learn page. */
function RoleHelp({ help }) {
  if (!help) return null;
  return (
    <details className="text-[13.5px] text-ipc-ink" data-testid="review-role-help">
      <summary className="tap cursor-pointer font-semibold text-ipc-light">What this role can do</summary>
      <div className="mt-1 space-y-1 border-l-2 border-ipc-border pl-3">
        <p>{help.does}</p>
        <p><span className="font-semibold">What is at risk: </span>{help.risk}</p>
        {help.learnUrl ? <p><ExternalLink href={help.learnUrl} className="font-semibold text-ipc-light hover:underline">Read more on Microsoft Learn</ExternalLink></p> : null}
      </div>
    </details>
  );
}

/** The scope the assignment applies to, in words, with the raw directory scope behind a Details disclosure. */
function AppliesTo({ item }) {
  const [copied, setCopied] = useState(false);
  const scope = item.directoryScopeId;
  const copy = async () => {
    try { await navigator.clipboard.writeText(scope); setCopied(true); } catch { setCopied(false); }
  };
  return (
    <div className="text-[13px] text-ipc-muted">
      <p><span className="font-semibold text-ipc-ink">Applies to</span> {item.scopeLabel}</p>
      {scope ? (
        <details>
          <summary className="tap cursor-pointer font-semibold text-ipc-light">Details</summary>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Directory scope:</span>
            <code className="break-all rounded-sm bg-ipc-surface px-1.5 py-0.5 text-[12.5px] text-ipc-ink">{scope}</code>
            <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={copy}>Copy</button>
            {copied ? <span role="status" className="text-ipc-ink">Copied.</span> : null}
          </p>
        </details>
      ) : null}
    </div>
  );
}

function ReviewRow({ item, draft, kindShown, view, admin, own, actorName, showRoleHelp, checked, onToggle, onDraft, onSave, onChange, onDelegate, code, codeRef, onCode, onResend, requester, tenantName, search }) {
  const name = personName(item.holder, 'Unknown principal');
  const editing = draft?.editing === true || !item.decision;
  const decision = draft?.decision ?? null;
  const required = decision === 'not-sure' || (item.needsText === true && decision !== null) || (own && decision === 'keep');
  const unchecked = item.ownAccess === 'unchecked';
  const bulkOk = keepAllEligible([item]).eligible.length === 1;
  const byOther = admin && item.decidedBy && item.decidedBy !== actorName;
  return (
    <li className="card space-y-3" data-item={item.itemRef} data-testid={`review-item-${item.itemRef}`}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <div className="flex min-w-0 flex-1 gap-3">
          {!item.decision ? (
            <input
              type="checkbox" className="mt-1 h-4 w-4 shrink-0" checked={checked} onChange={onToggle} disabled={!bulkOk}
              aria-label={`Select ${name}, ${item.role}`} title={bulkOk ? undefined : view.bulkExcludedLine}
            />
          ) : <span className="w-4 shrink-0" aria-hidden="true" />}
          <div className="min-w-0">
            <p className="font-semibold text-ipc-ink">{name}</p>
            {item.holder?.upn ? <p className="break-all text-[12.5px] text-ipc-muted">{item.holder.upn}</p> : null}
            {own || unchecked ? (
              <p className="mt-1" data-testid="review-self-tag">
                <span className="inline-flex items-center gap-1.5 rounded-sm border border-ipc-border px-2.5 py-0.5 text-[12.5px] font-semibold text-ipc-ink">
                  <Dot tone={own ? 'warn' : 'info'} />{own ? 'Your own access' : 'May include your own access'}
                </span>
              </p>
            ) : null}
            {(item.signals ?? []).length ? (
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-ipc-ink" data-testid="review-signals">
                {item.signals.map((s) => <span key={s.code} className="inline-flex items-center gap-1.5"><Dot tone={s.risk ? 'warn' : 'off'} />{s.label}</span>)}
              </p>
            ) : null}
          </div>
        </div>
        <div className="min-w-0 md:w-[30%]">
          <p className="text-sm text-ipc-ink">{item.role}</p>
          <AppliesTo item={item} />
          {showRoleHelp ? <RoleHelp help={item.roleHelp} /> : null}
          {item.reviewer ? (
            <p className="mt-1 text-[13px] text-ipc-muted" data-testid="review-reviewer">
              Assigned to {item.assignedToMe ? 'you' : (item.reviewer.displayName ?? 'another reviewer')}
            </p>
          ) : null}
        </div>
        {kindShown ? <p className="text-sm text-ipc-ink md:w-20" data-testid="review-kind">{KIND_WORDS[item.kind] ?? item.kind}</p> : null}
        <p className="text-sm text-ipc-ink md:w-28"><span className="text-ipc-muted md:block md:text-[12.5px]">Last sign-in<span className="md:hidden">: </span></span>{lastSignInWords(item.lastSignInAt)}</p>
      </div>

      {item.note ? (
        <p className="border-l-2 border-ipc-border pl-3 text-[13.5px] text-ipc-ink" data-testid="review-note">
          <span className="font-semibold">Note from your administrator: </span>{item.note.text}{/[.!?]$/.test(item.note.text ?? '') ? '' : '.'}
          {item.note.reviewBy ? ` Review by ${dateOnlyWords(item.note.reviewBy)}.` : ''}
          {item.note.owner ? ` Owner: ${personName(item.note.owner)}.` : ''}
        </p>
      ) : null}
      {item.escalatedNote ? (
        <p className="border-l-2 border-ipc-border pl-3 text-[13.5px] text-ipc-ink" data-testid="review-escalated-note">
          <span className="font-semibold">Passed to you with this note: </span>{item.escalatedNote}
        </p>
      ) : null}

      {item.decision && item.selfReview === true && byOther ? (
        <p className="text-[13px] text-ipc-muted" data-testid="review-self-note">Self-review: the reviewer decided on their own access.</p>
      ) : null}

      {!editing ? (
        <p className="flex flex-wrap items-center gap-x-2 text-sm text-ipc-ink" data-testid="review-saved">
          <StatusWord tone="good" strong={false}>{item.onBehalfOf ? <OnBehalf item={item} /> : byOther ? savedLine(item.decision).replace(' by you', ` by ${item.decidedBy}`) : savedLine(item.decision)}</StatusWord>
          <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={onChange}>Change</button>
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div role="group" aria-label={`Decision for ${name}, ${item.role}`} className="inline-flex" data-testid="review-decision">
              {DECISION_ORDER.filter((d) => !(d === 'keep' && item.keepNeedsLink === true)).map((d) => {
                const on = decision === d;
                return (
                  <button
                    key={d} type="button" aria-pressed={on}
                    className={`-ml-px min-h-[36px] border px-3.5 text-sm font-semibold first:ml-0 first:rounded-l-sm last:rounded-r-sm ${on ? 'relative z-10 border-ipc-primary bg-ipc-primary text-white' : 'border-ipc-field bg-white text-ipc-ink hover:bg-ipc-surface'}`}
                    onClick={() => onDraft({ decision: d, reasonId: '', error: null })}
                  >
                    {DECISION_WORDS[d]}
                  </button>
                );
              })}
            </div>
            {onDelegate ? <button type="button" className="tap text-sm font-semibold text-ipc-light hover:underline" onClick={onDelegate}>Delegate</button> : null}
          </div>
          {item.keepNeedsLink === true ? (
            <p className="text-[13px] text-ipc-muted" data-testid="review-keep-needs-link">{KEEP_NEEDS_LINK}</p>
          ) : null}
          {decision ? (
            <>
              <Justification
                id={justId(item.itemRef)} decision={decision} draft={draft} reasons={decision === 'not-sure' ? [] : (view.quickReasons?.[decision] ?? [])}
                required={required} onChange={onDraft} label={`Why, for ${name}`} askWho={item.canAsk !== false && search !== null} requester={requester}
                search={search} askId={`review-ask-${item.itemRef}`}
              />
              {code ? (
                <div className="max-w-xs" data-testid="review-code">
                  <label className="label" htmlFor={`review-code-${item.itemRef}`}>We sent a 6-digit code to your email address</label>
                  <input
                    id={`review-code-${item.itemRef}`} ref={codeRef} className="input tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                    value={code.value} onChange={(e) => onCode(e.target.value.replace(/\D/g, ''))}
                  />
                  <p className="mt-1 text-[12.5px] text-ipc-muted">
                    Keeping this role needs the code: it proves the decision came from you. It expires in 10 minutes.{' '}
                    <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={onResend}>Send a new code</button>
                  </p>
                </div>
              ) : null}
              {draft?.error ? (
                <p role="alert" className="text-sm"><StatusWord tone="bad" strong={false}>{draft.error}</StatusWord></p>
              ) : null}
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" className="btn-primary min-h-[40px]" disabled={draft?.busy === true} onClick={onSave} data-testid="review-save">
                  {draft?.busy ? 'Saving' : decision === 'not-sure' ? 'Save and pass on' : 'Save'}
                </button>
                {draft?.editing ? <button type="button" className="btn-secondary min-h-[40px]" onClick={() => onDraft({ editing: false, error: null })}>Cancel</button> : null}
                <span className="text-[13px] text-ipc-muted">
                  {decision === 'remove' ? `A ${tenantName} administrator removes it in Microsoft Entra; onGuard checks it is gone.`
                    : decision === 'not-sure' ? `Goes to ${requester ? personName(requester) : 'an administrator'} with your note.`
                      : 'The assignment stays as it is.'}
                </span>
              </div>
            </>
          ) : null}
        </div>
      )}
    </li>
  );
}

/** "Kept by Megan Bowen (admin) for Miriam Graham, Oct 26: reason." An admin decided for the assigned reviewer. */
function OnBehalf({ item }) {
  const d = item.decision;
  const when = shortDate(d.decidedAt);
  const why = [d.reasonLabel, d.text].filter(Boolean).join(': ');
  return (
    <>
      <span data-testid="review-attribution">{BY_VERB[d.decision] ?? 'Decided'} by {item.decidedBy} (admin) for {item.onBehalfOf}</span>
      {when ? `, ${when}` : ''}{why ? `: ${why}` : ''}{/[.!?]$/.test(why) ? '' : '.'}
    </>
  );
}

/** Not sure: pick the colleague who could answer, or say you do not know who (it then goes to the administrators). */
function AskWho({ id, draft, onChange, requester, search }) {
  const noOne = draft?.noOne === true;
  return (
    <div className="space-y-1.5">
      <PeoplePicker
        id={id} label="Who could answer this?" search={search} value={draft?.askPeople ?? []} disabled={noOne}
        onChange={(askPeople) => onChange({ askPeople, error: null })}
        hint={`Type a name or an email address and pick the person: the assignment goes to them, with your note. Not sure who? It goes to ${requester ? personName(requester) : 'an administrator'} instead.`}
      />
      <button type="button" aria-pressed={noOne} className="tap text-sm font-semibold text-ipc-light hover:underline" onClick={() => onChange({ noOne: !noOne, askPeople: [], error: null })}>
        I don't know who
      </button>
    </div>
  );
}

function Justification({ id, decision, draft, reasons, required, onChange, label = 'Why', askWho = false, requester = null, search = null, askId = null }) {
  const textLabel = decision === 'not-sure'
    ? `Note for the next reviewer (required, at least ${TEXT_MIN} characters)`
    : required ? `Why (required: this assignment is flagged, at least ${TEXT_MIN} characters)` : 'Add a note (optional when you pick a reason)';
  return (
    <div className="space-y-2">
      {reasons.length ? (
        <fieldset>
          <legend className="sr-only">{label}: quick reason</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {reasons.map((r) => (
              <label key={r.id} className="inline-flex items-center gap-1.5 text-sm text-ipc-ink">
                <input type="radio" name={`${id}-reason`} value={r.id} checked={draft?.reasonId === r.id} onChange={() => onChange({ reasonId: r.id, error: null })} />
                {r.label}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
      {askWho && decision === 'not-sure' ? <AskWho id={askId ?? `${id}-ask`} draft={draft} onChange={onChange} requester={requester} search={search} /> : null}
      <label className="block">
        <span className="label">{textLabel}</span>
        <textarea id={id} className="input min-h-[72px]" maxLength={TEXT_MAX} value={draft?.text ?? ''} onChange={(e) => onChange({ text: e.target.value, error: null })} />
      </label>
    </div>
  );
}

function DelegateDialog({ itemRefs, adapter, describe = () => 'One assignment', onClose, onDone, onGone }) {
  const [people, setPeople] = useState([]);
  const [reason, setReason] = useState('');
  const [state, setState] = useState({ busy: false, error: null, failures: [] });
  const submit = async () => {
    const why = reason.trim();
    const problem = pillError(people);
    if (problem) { setState({ busy: false, error: `${problem} Remove them and choose someone else.` }); return; }
    const toId = pickedId(people);
    if (!toId) { setState({ busy: false, error: 'Choose your colleague: type a name or an email address and pick the person.' }); return; }
    if (why.length < 3) { setState({ busy: false, error: 'Say why your colleague should decide: it goes into the evidence.' }); return; }
    setState({ busy: true, error: null, failures: [] });
    // One call. The API checks and saves each item on its own and answers per item: `handed` moved, `refused` stayed
    // with its reason. Nothing is re-sent, so an item that already moved is never handed over twice.
    const perItem = (list) => (Array.isArray(list) ? list.filter((f) => typeof f?.itemRef === 'string' && itemRefs.includes(f.itemRef)) : []);
    try {
      const r = await adapter.delegate({ itemRefs, toId, reason: why });
      if (r.status === 200 && r.body?.state === 'delegated') {
        const handed = Array.isArray(r.body.handed) ? r.body.handed.filter((ref) => itemRefs.includes(ref)) : itemRefs;
        onDone(handed, r.body.to, perItem(r.body.refused).map((f) => ({ itemRef: f.itemRef, message: f.message ?? 'Not handed over.' })), r.body.emailRetry === true);
        return;
      }
      if (r.status === 410) { onGone?.(r.body); return; }
      const failures = itemRefs.length > 1 ? perItem(r.body?.refused).map((f) => ({ itemRef: f.itemRef, message: f.message ?? 'Not handed over.' })) : [];
      const message = failureWords(r, 'The assignments were not handed over. Check the person and try again.');
      setState({ busy: false, error: failures.length > 0 ? 'None of the assignments was handed over.' : message, failures });
    } catch {
      setState({ busy: false, error: 'The onGuard portal could not be reached. Try again.', failures: [] });
    }
  };
  return (
    <Dialog
      open onClose={onClose}
      title={`Delegate ${plural(itemRefs.length, 'assignment')}`}
      description="Your colleague gets one email and decides instead of you. They cannot pass it on again; they can choose Not sure."
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={state.busy} onClick={submit} data-testid="review-delegate-submit">{state.busy ? 'Delegating' : 'Delegate'}</button>
        </>
      )}
    >
      <PeoplePicker
        id="review-delegate-to" label="Colleague" value={people} onChange={(v) => { setPeople(v); setState((st) => ({ ...st, error: null })); }}
        // One item: its holder and role refine the suggestions. Several: no item, and the server checks each on Delegate.
        search={(q) => adapter.searchPeople({ q, ...(itemRefs.length === 1 ? { item: itemRefs[0] } : {}) })}
        hint="Type a name or an email address and pick the person from your directory."
      />
      <label className="block">
        <span className="label">Why (required)</span>
        <textarea className="input min-h-[72px]" maxLength={TEXT_MAX} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-delegate-reason" />
      </label>
      <DialogError message={state.error} />
      {state.failures?.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-5 text-sm text-ipc-ink" data-testid="review-delegate-dialog-failures">
          {state.failures.map((f) => <li key={f.itemRef}>{describe(f.itemRef)}: {f.message}</li>)}
        </ul>
      ) : null}
    </Dialog>
  );
}
